import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Radio as RadioIcon, Star, CalendarClock, Orbit, Play, Share2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useRadio } from "@/lib/RadioContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CocoonMark } from "@/components/CocoonMark";
import { Footer } from "@/components/Footer";
import { SocialInvite } from "@/components/SocialInvite";

const ICONS = [RadioIcon, Star, CalendarClock, Orbit];
const UI = {
  fr: { share: "Partager", copied: "Lien copié", back: "Retour" },
  en: { share: "Share", copied: "Link copied", back: "Back" },
  es: { share: "Compartir", copied: "Enlace copiado", back: "Volver" },
  nl: { share: "Delen", copied: "Link gekopieerd", back: "Terug" },
};
const rise = (i = 0) => ({ initial: { opacity: 0, y: 26 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.4 }, transition: { duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } });

export default function RadioBirth() {
  const { i18n } = useTranslation();
  const lang = ["fr", "en", "es", "nl"].includes((i18n.language || "fr").slice(0, 2)) ? (i18n.language || "fr").slice(0, 2) : "fr";
  const U = UI[lang];
  const [txt, setTxt] = useState(null);
  const r = useRadio();
  const navigate = useNavigate();

  useEffect(() => { api.get("/radio/birth", { params: { lang } }).then(({ data }) => setTxt(data)).catch(() => {}); }, [lang]);
  useEffect(() => { r.ensureLoaded(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const listen = () => { r.play(); navigate("/radio"); };
  const share = async () => {
    const url = `${window.location.origin}/radio/naissance`;
    try {
      if (navigator.share) { await navigator.share({ title: txt?.title, url }); return; }
      await navigator.clipboard.writeText(url); toast.success(U.copied);
    } catch { /* annulé */ }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tight"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/radio" className="text-sm text-foreground/60 hover:text-primary transition-colors" data-testid="birth-back-link">← {U.back}</Link>
          </div>
        </div>
      </header>

      <section className="relative h-[92vh] min-h-[560px] overflow-hidden" data-testid="birth-hero">
        <motion.img src="/derive/radio/naissance.jpg" alt="Radio Cocoon" initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 6, ease: "easeOut" }}
          className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/20 to-background" />
        <div className="relative h-full max-w-5xl mx-auto px-6 flex flex-col justify-end pb-20">
          {txt && (
            <>
              <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                className="inline-flex w-fit items-center gap-2 rounded-full bg-red-500/15 border border-red-400/40 px-3.5 py-1.5 text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-red-200 mb-5">
                <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> {txt.eyebrow}
              </motion.span>
              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 1 }}
                className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-3xl" data-testid="birth-title">{txt.title}</motion.h1>
            </>
          )}
          <ChevronDown className="absolute bottom-6 left-1/2 -translate-x-1/2 text-primary/70 animate-bounce" size={22} />
        </div>
      </section>

      {txt && (
        <main className="relative max-w-2xl w-full mx-auto px-6 pb-24" data-testid="birth-article">
          <motion.p {...rise()} className="font-display italic text-2xl sm:text-3xl text-primary leading-snug mb-10">{txt.paras[0]}</motion.p>
          {txt.paras.slice(1).map((t, i) => (
            <motion.p key={i} {...rise(i)} className={`text-foreground/80 text-base sm:text-lg leading-relaxed mb-6 ${i === 1 ? "font-display text-2xl sm:text-3xl text-foreground not-italic leading-snug my-10" : ""}`}>{t}</motion.p>
          ))}

          <motion.h2 {...rise()} className="mt-14 mb-6 text-xs uppercase tracking-[0.28em] text-primary/80">{txt.features_title}</motion.h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-14">
            {txt.features.map((f, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <motion.div key={i} {...rise(i)} className="glass rounded-2xl border border-primary/15 p-5 hover:border-primary/40 hover:-translate-y-1 transition-[transform,border-color]" data-testid={`birth-feature-${i}`}>
                  <Icon size={20} className="text-primary mb-3" />
                  <p className="text-sm text-foreground/80 leading-relaxed">{f}</p>
                </motion.div>
              );
            })}
          </div>

          {txt.closing.map((t, i) => (
            <motion.p key={i} {...rise(i)} className={i === txt.closing.length - 1 ? "font-display italic text-3xl sm:text-4xl text-primary leading-tight mt-10 mb-8" : "text-foreground/80 text-base sm:text-lg leading-relaxed mb-6"}>{t}</motion.p>
          ))}
          <motion.p {...rise()} className="font-display italic text-xl text-foreground/90 mb-14" data-testid="birth-signature">— {txt.signature}</motion.p>

          <motion.div {...rise()} className="flex flex-wrap items-center gap-4">
            <button onClick={listen} data-testid="birth-listen-btn"
              className="btn-frost-orange relative inline-flex items-center gap-3 rounded-full px-8 py-4 text-base font-semibold">
              <span className="absolute -inset-1 rounded-full border border-primary/40 animate-ping opacity-40" />
              <Play size={18} /> {txt.cta}
            </button>
            <button onClick={share} data-testid="birth-share-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3.5 text-sm text-foreground/75 hover:text-primary hover:border-primary/40 transition-colors">
              <Share2 size={15} /> {U.share}
            </button>
          </motion.div>
          <SocialInvite />
        </main>
      )}
      <Footer />
    </div>
  );
}
